// V1.0.64: Timeline 1 thiết bị 1 ngày - xem các session + chunks + nghe + xem transcript
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Empty, Spin, DatePicker, Button, Tag, Collapse, Tooltip, Drawer, App as AntdApp } from 'antd';
import { ArrowLeft, Play, Calendar, FileAudio, Clock, Activity, FileText, Download, Copy } from 'lucide-react';
import { voiceService, type VoiceRecord } from '@/services/voiceService';
import dayjs, { Dayjs } from 'dayjs';

function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBytes(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function statusTag(status: string) {
  switch (status) {
    case 'PENDING': return <Tag color="default" style={{ fontSize: 10 }}>Chờ xử lý</Tag>;
    case 'PROCESSING': return <Tag color="processing" style={{ fontSize: 10 }}>Đang transcribe</Tag>;
    case 'DONE': return <Tag color="green" style={{ fontSize: 10 }}>Đã xong</Tag>;
    case 'FAILED': return <Tag color="red" style={{ fontSize: 10 }}>Lỗi</Tag>;
    case 'NOT_REQUESTED': return <Tag style={{ fontSize: 10 }}>Không transcribe</Tag>;
    default: return <Tag style={{ fontSize: 10 }}>{status}</Tag>;
  }
}

export default function VoiceDeviceTimelinePage() {
  const { deviceCode } = useParams<{ deviceCode: string }>();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [chunkDrawer, setChunkDrawer] = useState<{ open: boolean; record: VoiceRecord | null }>({
    open: false, record: null,
  });
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // First resolve deviceCode → deviceId
  const { data: devices } = useQuery({
    queryKey: ['recording-devices-list-for-timeline'],
    queryFn: () => voiceService.listDevices({ approved: true }),
  });
  const device = devices?.find(d => d.deviceCode === deviceCode);

  const { data: timeline, isLoading } = useQuery({
    queryKey: ['voice-timeline', device?.id, date.format('YYYY-MM-DD')],
    queryFn: () => voiceService.getTimeline(device!.id, date.format('YYYY-MM-DD')),
    enabled: !!device,
  });

  const { data: segments } = useQuery({
    queryKey: ['voice-segments', chunkDrawer.record?.id],
    queryFn: () => voiceService.getSegments(chunkDrawer.record!.id),
    enabled: !!chunkDrawer.record,
  });

  // Khi drawer mở, fetch audio qua axios (có JWT) → blob URL
  useEffect(() => {
    if (!chunkDrawer.record) {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
      setAudioBlobUrl(null);
      return;
    }
    let cancelled = false;
    setAudioLoading(true);
    voiceService.fetchAudioBlobUrl(chunkDrawer.record.id)
      .then(url => {
        if (cancelled) {
          URL.revokeObjectURL(url);
        } else {
          // Revoke URL cũ nếu có
          if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
          setAudioBlobUrl(url);
        }
      })
      .catch(e => {
        if (!cancelled) message.error('Không tải được audio: ' + (e?.response?.data?.message ?? e.message));
      })
      .finally(() => { if (!cancelled) setAudioLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunkDrawer.record?.id]);

  const onDownload = async () => {
    if (!chunkDrawer.record) return;
    setDownloading(true);
    try {
      await voiceService.downloadAudio(chunkDrawer.record.id, chunkDrawer.record.fileName);
      message.success('Đã tải file');
    } catch (e: any) {
      message.error('Tải file thất bại: ' + (e?.response?.data?.message ?? e.message));
    } finally {
      setDownloading(false);
    }
  };

  const onCopyTranscript = () => {
    if (!chunkDrawer.record) return;
    const text = segments && segments.length > 0
      ? segments.map(s => {
          const m = Math.floor(s.startSec / 60);
          const ss = String(Math.floor(s.startSec % 60)).padStart(2, '0');
          return `[${m}:${ss}] ${s.text}`;
        }).join('\n')
      : (chunkDrawer.record.transcriptText ?? '');
    if (!text.trim()) {
      message.warning('Không có nội dung để copy');
      return;
    }
    navigator.clipboard.writeText(text);
    message.success('Đã copy transcript vào clipboard');
  };

  if (!device) {
    return (
      <div style={{ padding: 20 }}>
        {devices && <Empty description={`Không tìm thấy thiết bị có mã "${deviceCode}"`} />}
        {!devices && <Spin />}
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/voice-records/dashboard')}>
          Tổng quan
        </Button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>
            <span style={{ color: '#1976D2', fontFamily: 'monospace' }}>{device.deviceCode}</span>
            <span style={{ marginLeft: 12 }}>{device.deviceName}</span>
          </h2>
          <div style={{ color: '#7d8a98', fontSize: 13, marginTop: 4 }}>
            {device.location && <>📍 {device.location} · </>}
            {device.departmentName && <>🏥 {device.departmentName} · </>}
            {device.lastChunkAt && <>Chunk gần nhất: {dayjs(device.lastChunkAt).format('HH:mm DD/MM')}</>}
          </div>
        </div>
        <DatePicker
          value={date}
          onChange={(d) => d && setDate(d)}
          format="DD/MM/YYYY"
          allowClear={false}
        />
      </div>

      {isLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

      {timeline && (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <span style={{ color: '#7d8a98', fontSize: 12 }}>Tổng thời lượng:</span>{' '}
                <b style={{ fontSize: 16 }}>{formatDuration(timeline.totalDurationSec)}</b>
              </div>
              <div>
                <span style={{ color: '#7d8a98', fontSize: 12 }}>Tổng chunks:</span>{' '}
                <b style={{ fontSize: 16 }}>{timeline.totalChunks}</b>
              </div>
              <div>
                <span style={{ color: '#7d8a98', fontSize: 12 }}>Sessions:</span>{' '}
                <b style={{ fontSize: 16 }}>{timeline.sessions.length}</b>
              </div>
            </div>
          </Card>

          {timeline.sessions.length === 0 ? (
            <Empty description={`Không có ghi âm nào ngày ${date.format('DD/MM/YYYY')}`} />
          ) : (
            <Collapse defaultActiveKey={timeline.sessions.map((_, i) => i.toString())}>
              {timeline.sessions.map((session, idx) => (
                <Collapse.Panel
                  key={idx.toString()}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                      <Activity size={14} style={{ color: '#1976D2' }} />
                      <span style={{ fontWeight: 600 }}>
                        Session {idx + 1}: {dayjs(session.startedAt).format('HH:mm:ss')} - {dayjs(session.endedAt).format('HH:mm:ss')}
                      </span>
                      <span style={{ color: '#7d8a98', fontSize: 12, marginLeft: 'auto' }}>
                        {formatDuration(session.durationSec)} · {session.chunkCount} chunks
                      </span>
                    </div>
                  }
                >
                  {session.chunks.map(chunk => (
                    <div
                      key={chunk.id}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}
                    >
                      <FileAudio size={14} style={{ color: '#8a95a5' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13 }}>
                          <b>{dayjs(chunk.recordedAt).format('HH:mm:ss')}</b>{' '}
                          <span style={{ color: '#7d8a98' }}>
                            ({formatDuration(chunk.durationSec)} · {formatBytes(chunk.fileSize)})
                          </span>
                          {' '}{statusTag(chunk.transcriptStatus)}
                        </div>
                        {chunk.transcriptText && (
                          <div style={{
                            fontSize: 12, color: '#5b6875', marginTop: 4,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>
                            "{chunk.transcriptText.substring(0, 200)}..."
                          </div>
                        )}
                      </div>
                      <Tooltip title="Xem chi tiết + nghe">
                        <Button
                          size="small"
                          icon={<FileText size={14} />}
                          onClick={() => setChunkDrawer({ open: true, record: chunk })}
                        >
                          Chi tiết
                        </Button>
                      </Tooltip>
                    </div>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </>
      )}

      {/* Drawer xem chi tiết 1 chunk */}
      <Drawer
        title={chunkDrawer.record ? `Chunk ${dayjs(chunkDrawer.record.recordedAt).format('HH:mm:ss DD/MM/YYYY')}` : ''}
        open={chunkDrawer.open}
        onClose={() => setChunkDrawer({ open: false, record: null })}
        width={680}
      >
        {chunkDrawer.record && (
          <>
            <div style={{ marginBottom: 16 }}>
              {audioLoading ? (
                <div style={{ padding: 20, textAlign: 'center', background: '#fafafa', borderRadius: 4 }}>
                  <Spin size="small" /> <span style={{ marginLeft: 8, color: '#7d8a98' }}>Đang tải audio...</span>
                </div>
              ) : audioBlobUrl ? (
                <audio
                  controls
                  style={{ width: '100%' }}
                  src={audioBlobUrl}
                />
              ) : (
                <Empty description="Không tải được audio" />
              )}
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Button
                  icon={<Download size={14} />}
                  loading={downloading}
                  onClick={onDownload}
                  disabled={!audioBlobUrl}
                  size="small"
                >
                  Tải file audio
                </Button>
              </div>
            </div>

            <Card size="small" title="Thông tin" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                <div><b>Thời lượng:</b> {formatDuration(chunkDrawer.record.durationSec)}</div>
                <div><b>Kích thước:</b> {formatBytes(chunkDrawer.record.fileSize)}</div>
                <div><b>Định dạng:</b> {chunkDrawer.record.audioFormat}</div>
                <div><b>Engine:</b> {chunkDrawer.record.transcriptEngine ?? '—'}</div>
                <div><b>Độ tin cậy:</b> {chunkDrawer.record.transcriptConfidence != null
                  ? `${(chunkDrawer.record.transcriptConfidence * 100).toFixed(1)}%`
                  : '—'}</div>
                <div><b>Trạng thái:</b> {statusTag(chunkDrawer.record.transcriptStatus)}</div>
                {chunkDrawer.record.windowsUser && (
                  <div><b>Windows user:</b> {chunkDrawer.record.windowsUser}</div>
                )}
              </div>
            </Card>

            <Card
              size="small"
              title="Nội dung transcript"
              extra={
                chunkDrawer.record.transcriptStatus === 'DONE' && (
                  <Button size="small" icon={<Copy size={12} />} onClick={onCopyTranscript}>
                    Copy text
                  </Button>
                )
              }
            >
              {chunkDrawer.record.transcriptStatus !== 'DONE' ? (
                <Empty description={
                  chunkDrawer.record.transcriptStatus === 'PENDING' ? 'Đang chờ transcribe' :
                  chunkDrawer.record.transcriptStatus === 'PROCESSING' ? 'Đang transcribe' :
                  chunkDrawer.record.transcriptStatus === 'FAILED' ? 'Transcribe thất bại' :
                  'Chưa có nội dung'
                } />
              ) : segments && segments.length > 0 ? (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {segments.map(seg => (
                    <div
                      key={seg.id}
                      style={{
                        padding: '6px 0', borderBottom: '1px dashed #f0f0f0',
                        display: 'flex', gap: 12,
                      }}
                    >
                      <span style={{
                        fontFamily: 'monospace', fontSize: 11, color: '#8a95a5',
                        minWidth: 60, paddingTop: 2,
                      }}>
                        {Math.floor(seg.startSec / 60)}:{String(Math.floor(seg.startSec % 60)).padStart(2, '0')}
                      </span>
                      <span style={{ fontSize: 13, lineHeight: 1.5 }}>{seg.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {chunkDrawer.record.transcriptText ?? <em>Không có nội dung</em>}
                </div>
              )}
            </Card>
          </>
        )}
      </Drawer>
    </div>
  );
}
